// Shows.jsx - Gold shows in the dashboard. Making one, hanging photographs on it,
// and working the list of people who said they are coming.
//
// The screen is built around the day of the show rather than around the record.
// Standing at a stand with a phone, the two things anybody needs are the list of
// who is due and a way to tell them something, so those are the two things on the
// first screen and everything else is behind Edit.
//
// LEGEND
//   1. Shows        the list of shows
//   2. ShowEditor   the record: title, venue, days, photographs
//   3. People       who is coming, arriving, and messaging them
//   4. ShowDetail   the page that holds the two together

import { useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router";
import clsx from "clsx";

import { useAsync, useForm } from "../lib/hooks.js";
import { showsApi } from "../lib/api.js";
import { day as dayOf, ago } from "../lib/format.js";
import { Panel, Input, Textarea, Select, Button, Alert, Loading, Empty, Tag, Modal, Checkbox, Menu, useToast } from "../ui/kit.jsx";
import { Plus, Scale, Trash, Check, Message, Upload, ArrowLeft, Users } from "../ui/icons.jsx";

const dayName = (date) =>
  date ? new Date(`${date}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" }) : "";

const clockTime = (time) => {
  const [hour, minute] = String(time || "").split(":").map(Number);
  if (Number.isNaN(hour)) return "";
  const suffix = hour >= 12 ? "pm" : "am";
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return minute === 0 ? `${twelve}${suffix}` : `${twelve}:${String(minute).padStart(2, "0")}${suffix}`;
};

const STATUS_TONE = { draft: "neutral", published: "positive", cancelled: "critical" };

////////// 1. THE LIST //////////

export default function Shows() {
  const toast = useToast();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useAsync((signal) => showsApi.list(signal), []);
  const [creating, setCreating] = useState(false);

  const rows = data?.items || [];
  const upcoming = rows.filter((row) => row.upcoming);
  const past = rows.filter((row) => !row.upcoming);

  const create = async (values) => {
    const show = await showsApi.create(values);
    toast.success("Show created. It is a draft until you publish it.");
    navigate(`/admin/shows/${show.slug}`);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px]">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="display display-lg text-ink">Shows</h1>
          <p className="hint mt-1">Buying events, and the people who told us they are coming.</p>
        </div>
        <Button onClick={() => setCreating(true)}><Plus size={16} /> New show</Button>
      </header>

      {error && <Alert tone="error" className="mb-4">{error.message}</Alert>}

      {loading ? <Loading /> : rows.length === 0 ? (
        <Empty title="No shows yet" icon={Scale} action={<Button onClick={() => setCreating(true)}>Create the first one</Button>}>
          A show is one event at one venue. Create it as a draft, add the days and the address, then publish it when you
          are ready for people to see it.
        </Empty>
      ) : (
        <div className="space-y-8">
          {[["Coming up", upcoming], ["Past", past]].map(([label, list]) => list.length > 0 && (
            <section key={label}>
              <p className="eyebrow mb-3">{label}</p>
              <Panel className="overflow-hidden max-sm:border-0 max-sm:bg-transparent">
                <div className="sm:overflow-x-auto">
                  <table className="grid-table stack-table w-full sm:min-w-[760px]">
                    <thead>
                      <tr>
                        <th>Show</th>
                        <th>Where</th>
                        <th>When</th>
                        <th className="text-right">Coming</th>
                        <th className="text-right">Photos</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {list.map((show) => (
                        <tr key={show.slug} className="group">
                          <td data-label="Show">
                            <Link to={`/admin/shows/${show.slug}`} className="font-semibold text-ink hover:text-bronze-dark">
                              {show.title}
                            </Link>
                            <Tag tone={STATUS_TONE[show.status]} className="ml-2">{show.status}</Tag>
                          </td>
                          <td className="text-muted stack-hide" data-label="Where">
                            {[show.city, show.state].filter(Boolean).join(", ") || "—"}
                          </td>
                          <td className="text-muted whitespace-nowrap" data-label="When">
                            {show.days.length ? show.days.map(dayName).join(" to ") : "No dates yet"}
                          </td>
                          <td className="text-right num font-semibold text-ink" data-label="Coming">
                            {show.counts.people || "—"}
                          </td>
                          <td className="text-right num text-faint stack-hide" data-label="Photos">{show.photos || "—"}</td>
                          <td className="text-right" data-label="">
                            <Link to={`/admin/shows/${show.slug}`} className="btn btn-quiet btn-sm">Open</Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </section>
          ))}
        </div>
      )}

      <NewShow open={creating} onClose={() => setCreating(false)} onCreate={create} />
    </div>
  );
}

function NewShow({ open, onClose, onCreate }) {
  const form = useForm({ title: "", venue: { city: "", state: "" } });
  const submit = form.submit(async (values) => {
    await onCreate(values);
    onClose();
    return true;
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New show"
      description="Name it and say where. Everything else is on the next screen."
      footer={<><Button variant="quiet" onClick={onClose}>Cancel</Button><Button onClick={submit} loading={form.busy}>Create</Button></>}
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Input label="Name" placeholder="Autumn gold show" required {...form.field("title")} />
        <div className="grid grid-cols-3 gap-4">
          <Input label="City" className="col-span-2" {...form.field("venue.city")} />
          <Input label="State" maxLength={2} {...form.field("venue.state")} />
        </div>
        <p className="hint">The web address is made from the name and the city, and it never changes afterwards.</p>
      </form>
    </Modal>
  );
}

////////// 2. THE RECORD //////////

function ShowEditor({ show, onSaved }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const form = useForm({
    title: show.title || "",
    status: show.status || "draft",
    summary: show.summary || "",
    body: show.body || "",
    venue: {
      name: show.venue?.name || "", line1: show.venue?.line1 || "", line2: show.venue?.line2 || "",
      city: show.venue?.city || "", state: show.venue?.state || "", postal: show.venue?.postal || "",
      directions: show.venue?.directions || "",
    },
    slotMinutes: show.slotMinutes || 30,
    capacityPerSlot: show.capacityPerSlot || 0,
    internalNotes: show.internalNotes || "",
  });
  const [days, setDays] = useState(show.days?.length ? show.days.map((d) => ({ ...d })) : [{ date: "", opens: "10:00", closes: "17:00" }]);

  const save = form.submit(async (values) => {
    const updated = await showsApi.update(show.slug, {
      ...values,
      slotMinutes: Number(values.slotMinutes) || 30,
      capacityPerSlot: Number(values.capacityPerSlot) || 0,
      days: days.filter((entry) => entry.date),
    });
    toast.success("Saved");
    onSaved(updated);
    return updated;
  });

  const addPhotos = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      onSaved(await showsApi.addPhotos(show.slug, files));
      toast.success(`${files.length} photograph${files.length === 1 ? "" : "s"} added`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <Panel className="p-5 space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <p className="eyebrow">The show</p>
          <Select
            className="w-auto"
            options={[
              { value: "draft", label: "Draft, nobody can see it" },
              { value: "published", label: "Published, live on the site" },
              { value: "cancelled", label: "Cancelled" },
            ]}
            {...form.field("status")}
          />
        </div>

        <Input label="Name" required {...form.field("title")} />
        <Input label="One line for the card" hint="Shown on the list and in search results" {...form.field("summary")} />
        <Textarea label="Description" rows={5} hint="Plain text. Line breaks are kept." {...form.field("body")} />
      </Panel>

      <Panel className="p-5 space-y-4">
        <p className="eyebrow">Where</p>
        <Input label="Venue" placeholder="Ramada Conference Center" {...form.field("venue.name")} />
        <Input label="Street" {...form.field("venue.line1")} />
        <Input label="Room, floor or suite" {...form.field("venue.line2")} />
        <div className="grid grid-cols-4 gap-4">
          <Input label="City" className="col-span-2" {...form.field("venue.city")} />
          <Input label="State" maxLength={2} {...form.field("venue.state")} />
          <Input label="ZIP" {...form.field("venue.postal")} />
        </div>
        <Textarea
          label="Directions"
          rows={2}
          hint="The sentence that actually helps. Which entrance, where to park, which hall."
          {...form.field("venue.directions")}
        />
      </Panel>

      <Panel className="p-5 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <p className="eyebrow">Days and hours</p>
          <Button size="sm" variant="quiet" onClick={() => setDays([...days, { date: "", opens: "10:00", closes: "17:00" }])}>
            <Plus size={14} /> Add a day
          </Button>
        </div>
        <p className="hint">The arrival times people can pick are made from these hours, so there is no second list to keep in step.</p>

        {days.map((entry, index) => (
          <div key={index} className="grid grid-cols-[minmax(0,1fr)_auto_auto_auto] gap-3 items-end">
            <Input label={index === 0 ? "Date" : undefined} type="date" value={entry.date}
              onChange={(event) => setDays(days.map((d, i) => i === index ? { ...d, date: event.target.value } : d))} />
            <Input label={index === 0 ? "Opens" : undefined} type="time" value={entry.opens}
              onChange={(event) => setDays(days.map((d, i) => i === index ? { ...d, opens: event.target.value } : d))} />
            <Input label={index === 0 ? "Closes" : undefined} type="time" value={entry.closes}
              onChange={(event) => setDays(days.map((d, i) => i === index ? { ...d, closes: event.target.value } : d))} />
            <Button size="sm" variant="ghost" onClick={() => setDays(days.filter((_, i) => i !== index))} aria-label="Remove this day">
              <Trash size={15} />
            </Button>
          </div>
        ))}

        <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-rule-soft">
          <Input label="Minutes per arrival time" type="number" min="5" max="240" step="5" {...form.field("slotMinutes")} />
          <Input label="Most people per time" type="number" min="0" hint="Zero is unlimited, which is normal" {...form.field("capacityPerSlot")} />
        </div>
      </Panel>

      <Panel className="p-5 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <p className="eyebrow">Photographs</p>
          <label className="btn btn-quiet btn-sm cursor-pointer">
            <Upload size={14} /> {busy ? "Uploading" : "Add photographs"}
            <input type="file" accept="image/*" multiple className="sr-only" onChange={(event) => addPhotos([...event.target.files])} />
          </label>
        </div>
        <p className="hint">The first one becomes the card image. These show on the show's own page and are the best argument for the next one.</p>

        {show.gallery?.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2.5">
            {show.gallery.map((photo, index) => (
              <div key={index} className="relative group">
                <img src={photo.url} alt="" className="w-full aspect-square object-cover border border-rule" />
                <button
                  type="button"
                  className="absolute top-1 right-1 p-1 bg-paper border border-rule opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Remove this photograph"
                  onClick={async () => { onSaved(await showsApi.removePhoto(show.slug, index)); }}
                >
                  <Trash size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel className="p-5 space-y-4">
        <p className="eyebrow">Internal</p>
        <Textarea label="Notes" rows={3} hint="Never shown to anybody outside the building." {...form.field("internalNotes")} />
      </Panel>

      {form.message && <Alert tone="error">{form.message.text}</Alert>}
      <Button onClick={save} loading={form.busy} size="lg">Save the show</Button>
    </div>
  );
}

////////// 3. WHO IS COMING //////////

function People({ show, onChanged }) {
  const toast = useToast();
  const [picked, setPicked] = useState(() => new Set());
  const [messaging, setMessaging] = useState(false);

  const people = [...(show.registrations || [])]
    .filter((entry) => entry.status !== "cancelled")
    .sort((a, b) => `${a.day} ${a.time}`.localeCompare(`${b.day} ${b.time}`));

  const setStatus = async (person, status) => {
    try {
      onChanged(await showsApi.setPerson(show.slug, person.id, { status }));
    } catch (error) {
      toast.error(error.message);
    }
  };

  const toggle = (id) => setPicked((previous) => {
    const next = new Set(previous);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  if (!people.length) {
    return (
      <Empty title="Nobody has registered yet" icon={Users}>
        People can turn up without registering, so an empty list is not an empty show. It fills up once the page has
        been live for a few days.
      </Empty>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="hint">
          <span className="num font-semibold text-ink">{show.counts.people}</span> people across{" "}
          <span className="num">{people.length}</span> registrations.{" "}
          <span className="num">{show.counts.arrived}</span> arrived.{" "}
          <span className="num">{show.counts.textable}</span> can be texted.
        </p>
        <Button size="sm" onClick={() => setMessaging(true)}>
          <Message size={15} /> Message {picked.size ? `${picked.size} selected` : "everyone"}
        </Button>
      </div>

      <Panel className="overflow-hidden max-sm:border-0 max-sm:bg-transparent">
        <div className="sm:overflow-x-auto">
          <table className="grid-table stack-table w-full sm:min-w-[820px]">
            <thead>
              <tr>
                <th className="w-9" />
                <th>When</th>
                <th>Who</th>
                <th>Bringing</th>
                <th>Status</th>
                <th className="text-right" />
              </tr>
            </thead>
            <tbody>
              {people.map((person) => (
                <tr key={person.id} className={clsx("group", picked.has(person.id) && "bg-surface")}>
                  <td data-label="">
                    <Checkbox checked={picked.has(person.id)} onChange={() => toggle(person.id)} aria-label={`Select ${person.name}`} />
                  </td>
                  <td className="whitespace-nowrap" data-label="When">
                    <span className="text-ink font-semibold num">{clockTime(person.time)}</span>
                    <span className="block text-[12px] text-faint">{dayName(person.day)}</span>
                  </td>
                  <td data-label="Who">
                    <span className="block text-ink">{person.name}{person.people > 1 ? ` +${person.people - 1}` : ""}</span>
                    <span className="block text-[12px] text-faint num">{person.phone || person.email}</span>
                  </td>
                  <td className="text-muted stack-hide" data-label="Bringing">
                    <span className="line-clamp-2">{person.bringing || "—"}</span>
                  </td>
                  <td data-label="Status">
                    <Tag tone={person.status === "arrived" ? "positive" : person.status === "no_show" ? "neutral" : "caution"}>
                      {person.status === "no_show" ? "no show" : person.status}
                    </Tag>
                  </td>
                  <td className="text-right" data-label="">
                    <Menu
                      label="Mark"
                      items={[
                        { label: "Arrived", icon: Check, onSelect: () => setStatus(person, "arrived") },
                        { label: "Did not come", onSelect: () => setStatus(person, "no_show") },
                        { label: "Back to registered", onSelect: () => setStatus(person, "registered") },
                        { label: "Remove from the list", tone: "critical", onSelect: () => setStatus(person, "cancelled") },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <MessageRoom
        show={show}
        open={messaging}
        ids={[...picked]}
        onClose={() => setMessaging(false)}
        onSent={() => { setPicked(new Set()); onChanged(); }}
      />
    </div>
  );
}

function MessageRoom({ show, open, ids, onClose, onSent }) {
  const toast = useToast();
  const form = useForm({ subject: `About ${show.title}`, text: "", email: true, sms: false });

  const send = form.submit(async (values) => {
    const channels = [values.email && "email", values.sms && "sms"].filter(Boolean);
    if (!channels.length) throw new Error("Pick email, text, or both");

    const result = await showsApi.message(show.slug, { subject: values.subject, text: values.text, channels, ids });
    toast.success(`Sent to ${result.emailed + result.texted} of ${result.asked}`);
    onSent();
    onClose();
    return result;
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={ids.length ? `Message ${ids.length} people` : "Message everyone coming"}
      description="Goes out as written. Only people who ticked the box get a text, and that is decided per person."
      footer={<><Button variant="quiet" onClick={onClose}>Cancel</Button><Button onClick={send} loading={form.busy}>Send it</Button></>}
    >
      <form onSubmit={send} className="space-y-4" noValidate>
        <Input label="Subject" hint="Email only" {...form.field("subject")} />
        <Textarea label="Message" rows={5} required placeholder="The doors open an hour late today. We are still here until five." {...form.field("text")} />
        <div className="flex gap-5">
          <Checkbox label="Email" checked={form.values.email} onChange={(event) => form.set("email", event.target.checked)} />
          <Checkbox label="Text" hint={`${show.counts.textable} agreed to texts`} checked={form.values.sms} onChange={(event) => form.set("sms", event.target.checked)} />
        </div>
        {form.message && <Alert tone="error">{form.message.text}</Alert>}
      </form>
    </Modal>
  );
}

////////// 4. THE PAGE //////////

export function ShowDetail() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "people";
  const { data: show, loading, error, setData, reload } = useAsync((signal) => showsApi.get(slug, signal), [slug]);

  if (loading) return <Loading label="Loading the show" />;
  if (error) return <div className="p-8"><Alert tone="error" title="Could not load that show">{error.message}</Alert></div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]">
      <Link to="/admin/shows" className="inline-flex items-center gap-1.5 text-[13px] text-muted hover:text-ink mb-5">
        <ArrowLeft size={15} /> All shows
      </Link>

      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="eyebrow">
            {show.days?.map((entry) => dayName(entry.date)).join(" to ") || "No dates yet"}
            {show.venue?.city ? ` · ${show.venue.city}, ${show.venue.state}` : ""}
          </p>
          <h1 className="display display-lg text-ink mt-1">{show.title}</h1>
        </div>
        <div className="flex items-center gap-2.5">
          <Tag tone={STATUS_TONE[show.status]}>{show.status}</Tag>
          {show.status === "published" && (
            <a href={`/shows/${show.slug}`} target="_blank" rel="noopener noreferrer" className="btn btn-quiet btn-sm">
              View the page
            </a>
          )}
        </div>
      </header>

      <div className="chip-row mb-6">
        {[["people", `Who is coming (${show.counts?.people || 0})`], ["edit", "Edit the show"]].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setParams(key === "people" ? {} : { tab: key }, { replace: true })}
            className={clsx("btn btn-sm", tab === key ? "btn-ink" : "btn-quiet")}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "people"
        ? <People show={show} onChanged={(updated) => (updated ? setData(updated) : reload())} />
        : <ShowEditor show={show} onSaved={setData} />}
    </div>
  );
}
